<script lang="ts" setup>
import { type ProductenProductsGetGetProductenForAdvisorByPakketProductDto } from '@services/producten-public/orval-api/model/productenProductsGetGetProductenForAdvisorByPakketProductDto';
import { type SUREbusinessCommonDomainDoelgroepEnum as Doelgroep } from '@services/producten-public/orval-api/model/sUREbusinessCommonDomainDoelgroepEnum';
import { usePakkettenGetPakket } from '@services/producten-public/orval-api/pakketten-api/pakketten-api';
import { useProductenGetByPakket } from '@services/producten-public/orval-api/producten-api/producten-api';
import ExpandTransition from '@shared/animation/transitions/ExpandTransition/ExpandTransition.vue';
import VInfoBox from '@shared/component/VInfoBox/VInfoBox.vue';
import VSearch from '@shared/component/VSearch/VSearch.vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import VStatusTag from '@shared/component/VStatusTag/VStatusTag.vue';
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { computed, nextTick, ref, toRefs, useSlots, watch } from 'vue';

const props = defineProps<{
  pakketId?: string | null;
  selectedProducts: Record<string, number>;
  currentProducts: Record<string, number>;
  adviseurnummer?: number;
  addRequiredProducts?: boolean;
  doelgroepen?: Array<{ code: string; omschrijving: string }>;
}>();

const emit = defineEmits<{ productIncreased: [string]; productDecreased: [string] }>();

const slot = useSlots();

const doelgroep = defineModel<Doelgroep>('doelgroep');

const { pakketId, adviseurnummer, addRequiredProducts } = toRefs(props);

const { data: producten, isPending: productenPending } = useProductenGetByPakket(
  computed(() => ({ pakketId: pakketId.value!, adviseurnummer: adviseurnummer.value })),
  {
    query: {
      enabled: () => !!pakketId.value,
      select: (q) => q.result,
      staleTime: 30 * 1000,
    },
  },
);

watch(
  () => producten.value,
  async () => {
    if (producten.value && addRequiredProducts.value === true) {
      await nextTick();
      producten.value
        .flatMap((x) => x.products)
        .filter((x) => x.isRequired)
        .forEach((p) => emit('productIncreased', p.productId));
    }
  },
);

const { data: pakket, isPending: pakketPending } = usePakkettenGetPakket(
  computed(() => pakketId.value),
  {
    query: {
      select: (q) => q.result,
    },
  },
);

const numberOfProducts = computed(() =>
  Object.values(props.selectedProducts).reduce((total, num) => total + num, 0),
);

const numberOfExistingProducts = computed(() =>
  Object.values(props.currentProducts).reduce((total, num) => total + num, 0),
);

const maximumAantalProductenOverschreden = computed(
  () =>
    pakket.value?.maximumAantalProducten &&
    numberOfProducts.value + numberOfExistingProducts.value >= pakket.value.maximumAantalProducten,
);

const searchTerm = ref<string>();

const filteredGroups = computed(() =>
  producten.value
    ?.map((g) => ({
      ...g,
      products: g.products.filter(
        (p) =>
          (!searchTerm.value ||
            p.omschrijving.toLowerCase().includes(searchTerm.value.toLowerCase())) &&
          (!doelgroep.value || p.doelgroep.includes(doelgroep.value)),
      ),
    }))
    .filter((g) => g.products.length > 0),
);

function decrease(product: ProductenProductsGetGetProductenForAdvisorByPakketProductDto) {
  if (product.isRequired) return;
  emit('productDecreased', product.productId);
}

function increase(product: ProductenProductsGetGetProductenForAdvisorByPakketProductDto) {
  if (!allowIncrease(product)) return;
  emit('productIncreased', product.productId);
}

function allowIncrease(product: ProductenProductsGetGetProductenForAdvisorByPakketProductDto) {
  if (maximumAantalProductenOverschreden.value) return false;
  const { multipleAllowed } = product;
  const total =
    (props.selectedProducts[product.productId] ?? 0) +
    (props.currentProducts[product.productId] ?? 0);
  if (!multipleAllowed) return total === 0;
  return true;
}
</script>
<template>
  <div>
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-2 lg:flex-row">
        <div class="basis-1/2" v-if="slot.start">
          <slot name="start"></slot>
        </div>
        <div
          v-if="doelgroepen && doelgroepen.length > 1"
          class="flex grow basis-1/2 flex-col rounded-lg"
        >
          <span class="mb-3 text-sm font-bold text-primary-900"> Doelgroep </span>

          <ExpandTransition
            class="flex flex-wrap gap-2 text-sm"
            direction="horizontal"
            group
            tag="div"
          >
            <button
              v-for="dg in doelgroepen"
              @click="doelgroep = dg.code as Doelgroep"
              type="button"
              class="rounded-lg border border-grey-200 px-3 py-2"
              :class="{
                'bg-grey-200 font-bold': dg.code === doelgroep,
                'hover:bg-grey-100': dg.code !== doelgroep,
              }"
              :key="dg.code"
            >
              {{ dg.omschrijving }}
            </button>
          </ExpandTransition>
        </div>
      </div>

      <VSearch @input="(e) => (searchTerm = e)" placeholder="Product zoeken.." />

      <VSkeleton :count="10" height="lg" v-if="productenPending || pakketPending" />

      <VInfoBox v-if="filteredGroups?.length === 0"
        >Er zijn geen producten die voldoen aan de criteria.</VInfoBox
      >
      <div
        :key="group.productGroupId"
        v-for="group in filteredGroups"
        class="rounded-lg border border-grey-100 p-3.5"
      >
        <span class="text-sm font-bold uppercase text-primary">{{ group.name }}</span>
        <div class="flex flex-col gap-0.5 pt-3">
          <div
            @click="increase(product)"
            :key="product.productId"
            v-for="product in group.products"
            :class="{ 'bg-grey-100': selectedProducts[product.productId] }"
            class="flex cursor-pointer items-center justify-between gap-2 rounded-lg px-2 py-1 hover:bg-grey-100"
          >
            <div class="flex items-center gap-1">
              <span :class="{ 'font-bold': selectedProducts[product.productId] }">{{
                product.omschrijving
              }}</span>
              <VStatusTag v-if="product.isTest" color="orange">Test</VStatusTag>
            </div>
            <div class="flex items-center gap-2">
              <span
                v-if="selectedProducts[product.productId]"
                class="flex size-6 justify-center rounded-full bg-primary font-bold text-white"
              >
                {{ selectedProducts[product.productId] }}
              </span>
              <div
                @click.stop="decrease(product)"
                class="h-full"
                :class="{ 'cursor-no-drop': product.isRequired }"
              >
                <FontAwesomeIcon
                  :class="{
                    'text-red': !product.isRequired,
                    'text-grey-300': product.isRequired,
                  }"
                  v-if="selectedProducts[product.productId]"
                  icon="far fa-minus"
                />
              </div>
              <FontAwesomeIcon
                :class="{
                  'text-primary': allowIncrease(product),
                  'cursor-no-drop text-grey-300': !allowIncrease(product),
                }"
                icon="far fa-plus"
              />
            </div>
          </div>
        </div>
      </div>
      <VInfoBox
        size="small"
        class="sticky bottom-0"
        v-if="maximumAantalProductenOverschreden"
        type="info"
      >
        Er kunnen geen producten meer toegevoegd worden omdat het maximum van
        {{ pakket?.maximumAantalProducten }} is bereikt.
      </VInfoBox>
    </div>
  </div>
</template>
